import type {
  Bubble,
  Bullet,
  CoreMatrix,
  EngineInput,
  EngineState,
  Enemy,
  EnemyType,
  GameStatus,
  Particle,
  Player,
  Shockwave,
} from './types';
import { circleCircleIntersect, circleBoxIntersect, clamp } from './collision';

export class GameEngine {
  readonly worldWidth: number;
  readonly worldHeight: number;

  private status: GameStatus = 'start';
  private score = 0;
  private combo = 1;
  private comboTimer = 0;
  private lives = 3;
  private wave = 1;
  private message: string | undefined = 'PRESS FIRE / R TO START';
  private messageTimer = 3.0;

  private player!: Player;
  private core!: CoreMatrix;
  private enemies: Enemy[] = [];
  private bubbles: Bubble[] = [];
  private bullets: Bullet[] = [];
  private shockwaves: Shockwave[] = [];
  private particles: Particle[] = [];

  private fireCooldown = 0;
  private enemySpawnTimer = 0;
  private enemiesToSpawnInWave = 0;
  private idCounter = 0;

  constructor(worldWidth = 800, worldHeight = 600) {
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
    this.reset();
  }

  private nextId(prefix: string): string {
    this.idCounter++;
    return `${prefix}_${this.idCounter}`;
  }

  reset(): void {
    this.status = 'start';
    this.score = 0;
    this.combo = 1;
    this.comboTimer = 0;
    this.lives = 3;
    this.wave = 1;
    this.message = 'PRESS FIRE / R TO START';
    this.messageTimer = 3.0;
    this.fireCooldown = 0;
    this.enemySpawnTimer = 0;

    this.core = {
      x: this.worldWidth / 2,
      y: this.worldHeight - 35,
      width: 180,
      height: 30,
      health: 100,
      maxHealth: 100,
    };

    this.player = {
      x: this.worldWidth / 2,
      y: this.worldHeight - 100,
      vx: 0,
      vy: 0,
      radius: 16,
      angle: -Math.PI / 2, // facing up
      invulnTimer: 0,
      recoilVx: 0,
      recoilVy: 0,
    };

    this.enemies = [];
    this.bubbles = [];
    this.bullets = [];
    this.shockwaves = [];
    this.particles = [];
  }

  start(): void {
    this.reset();
    this.status = 'playing';
    this.message = `WAVE ${this.wave}`;
    this.messageTimer = 2.0;
    this.setupWave(this.wave);
  }

  restart(): void {
    this.start();
  }

  private setupWave(waveNum: number): void {
    this.enemiesToSpawnInWave = 6 + waveNum * 4;
    this.enemySpawnTimer = 0.5;
  }

  getState(): EngineState {
    return {
      status: this.status,
      score: this.score,
      combo: this.combo,
      comboTimer: this.comboTimer,
      lives: this.lives,
      wave: this.wave,
      message: this.messageTimer > 0 ? this.message : undefined,
      messageTimer: this.messageTimer,
      worldWidth: this.worldWidth,
      worldHeight: this.worldHeight,
      player: { ...this.player },
      enemies: this.enemies.map((e) => ({ ...e })),
      bubbles: this.bubbles.map((b) => ({ ...b })),
      bullets: this.bullets.map((b) => ({ ...b })),
      shockwaves: this.shockwaves.map((s) => ({ ...s })),
      particles: this.particles.map((p) => ({ ...p })),
      core: { ...this.core },
    };
  }

  getHud() {
    return {
      score: this.score,
      lives: Math.max(0, this.lives),
      wave: this.wave,
      message: this.messageTimer > 0 ? this.message : undefined,
    };
  }

  tick(dt: number, input: EngineInput, audioEnergy = 0): void {
    if (this.messageTimer > 0) {
      this.messageTimer -= dt;
    }

    if (this.fireCooldown > 0) {
      this.fireCooldown -= dt;
    }

    // Handle Start & Game Over restart inputs
    if (this.status === 'start') {
      if (input.firePressed || input.restartPressed || input.fire) {
        this.start();
      }
      return;
    }

    if (this.status === 'gameover') {
      if (input.firePressed || input.restartPressed) {
        this.restart();
      }
      return;
    }

    // Manage combo timer
    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) {
        this.combo = 1;
      }
    }

    // Update Player position and recoil
    this.updatePlayer(dt, input);

    // Shooting
    if ((input.firePressed || input.fire) && this.fireCooldown <= 0) {
      this.firePulse();
      this.fireCooldown = 0.22;
    }

    // Wave Spawning
    if (this.enemiesToSpawnInWave > 0) {
      this.enemySpawnTimer -= dt;
      if (this.enemySpawnTimer <= 0) {
        this.spawnEnemyForWave();
        this.enemySpawnTimer = Math.max(0.4, 1.8 - this.wave * 0.15 - audioEnergy * 0.5);
      }
    }

    // Update Bullets
    this.updateBullets(dt);

    // Update Enemies
    this.updateEnemies(dt, audioEnergy);

    // Update Bubbles
    this.updateBubbles(dt);

    // Update Shockwaves
    this.updateShockwaves(dt);

    // Update Particles
    this.updateParticles(dt);

    // Check Collisions
    this.checkCollisions();

    // Check Wave Progression
    if (
      this.enemiesToSpawnInWave <= 0 &&
      this.enemies.length === 0 &&
      this.bubbles.length === 0
    ) {
      this.score += 250 * this.wave;
      this.wave++;
      this.message = `WAVE ${this.wave} CLEAR BONUS!`;
      this.messageTimer = 2.0;
      this.setupWave(this.wave);
    }
  }

  private updatePlayer(dt: number, input: EngineInput): void {
    if (this.player.invulnTimer > 0) {
      this.player.invulnTimer -= dt;
    }

    const moveSpeed = 380;
    let dx = 0;
    let dy = 0;

    if (input.left) dx -= 1;
    if (input.right) dx += 1;
    if (input.up) dy -= 1;
    if (input.down) dy += 1;

    if (dx !== 0 && dy !== 0) {
      const len = Math.sqrt(dx * dx + dy * dy);
      dx /= len;
      dy /= len;
    }

    this.player.vx = dx * moveSpeed;
    this.player.vy = dy * moveSpeed;

    // Decay recoil forces
    this.player.recoilVx *= Math.pow(0.01, dt);
    this.player.recoilVy *= Math.pow(0.01, dt);

    // Aim angle based on movement or default up
    if (dx !== 0 || dy !== 0) {
      this.player.angle = Math.atan2(dy, dx);
    }

    // Apply movement & recoil
    this.player.x += (this.player.vx + this.player.recoilVx) * dt;
    this.player.y += (this.player.vy + this.player.recoilVy) * dt;

    // Clamp player to arena limits
    const margin = this.player.radius;
    this.player.x = clamp(this.player.x, margin, this.worldWidth - margin);
    this.player.y = clamp(this.player.y, margin, this.worldHeight - margin - 40);
  }

  private firePulse(): void {
    const bulletSpeed = 650;
    const angle = this.player.angle;
    const vx = Math.cos(angle) * bulletSpeed;
    const vy = Math.sin(angle) * bulletSpeed;

    this.bullets.push({
      id: this.nextId('bullet'),
      x: this.player.x + Math.cos(angle) * 20,
      y: this.player.y + Math.sin(angle) * 20,
      vx,
      vy,
      radius: 6,
      life: 1.5,
      fromPlayer: true,
    });

    // Firing Recoil force!
    const recoilForce = 160;
    this.player.recoilVx -= Math.cos(angle) * recoilForce;
    this.player.recoilVy -= Math.sin(angle) * recoilForce;
  }

  private spawnEnemyForWave(): void {
    this.enemiesToSpawnInWave--;
    const rand = Math.random();
    let type: EnemyType = 'runner';
    if (rand < 0.25) type = 'heavy';
    else if (rand < 0.55) type = 'pulsar';

    const margin = 30;
    const spawnX = margin + Math.random() * (this.worldWidth - margin * 2);
    const spawnY = -20;

    let health = 1;
    let radius = 14;
    let speed = 120 + this.wave * 10;
    let color = '#00f3ff';

    if (type === 'heavy') {
      health = 3;
      radius = 22;
      speed = 60 + this.wave * 5;
      color = '#ff0055';
    } else if (type === 'pulsar') {
      health = 2;
      radius = 16;
      speed = 90 + this.wave * 8;
      color = '#ffcc00';
    }

    // Vector towards Core
    const targetX = this.core.x + (Math.random() - 0.5) * (this.core.width * 0.8);
    const targetY = this.core.y;
    const dx = targetX - spawnX;
    const dy = targetY - spawnY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    this.enemies.push({
      id: this.nextId('enemy'),
      x: spawnX,
      y: spawnY,
      vx: (dx / dist) * speed,
      vy: (dy / dist) * speed,
      radius,
      type,
      health,
      maxHealth: health,
      trapped: false,
      trappedTimer: 0,
      color,
    });
  }

  private updateBullets(dt: number): void {
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;

      if (
        b.life <= 0 ||
        b.x < -20 ||
        b.x > this.worldWidth + 20 ||
        b.y < -20 ||
        b.y > this.worldHeight + 20
      ) {
        this.bullets.splice(i, 1);
      }
    }
  }

  private updateEnemies(dt: number, audioEnergy: number): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      if (e.trapped) continue;

      // Pulsar behavior: periodic speed bursts
      let speedMult = 1;
      if (e.type === 'pulsar') {
        speedMult = 1 + Math.sin(performance.now() * 0.005) * 0.6 + audioEnergy * 0.5;
      }

      e.x += e.vx * speedMult * dt;
      e.y += e.vy * speedMult * dt;

      // Check reaching Core Matrix
      if (circleBoxIntersect(e, this.core)) {
        this.damageCore(15);
        this.spawnExplosion(e.x, e.y, e.color, 12);
        this.enemies.splice(i, 1);
      }
    }
  }

  private updateBubbles(dt: number): void {
    for (let i = this.bubbles.length - 1; i >= 0; i--) {
      const b = this.bubbles[i];

      if (b.popping) {
        b.popTimer += dt * 6;
        if (b.popTimer >= 1) {
          this.bubbles.splice(i, 1);
        }
        continue;
      }

      // Drift bubbles gently upward
      b.x += b.vx * dt;
      b.y += b.vy * dt;

      // Keep trapped enemy positioned inside bubble
      if (b.trappedEnemy) {
        b.trappedEnemy.x = b.x;
        b.trappedEnemy.y = b.y;
        b.trappedEnemy.trappedTimer += dt;

        // Auto escape after 8 seconds if not popped
        if (b.trappedEnemy.trappedTimer > 8.0) {
          b.trappedEnemy.trapped = false;
          this.enemies.push(b.trappedEnemy);
          this.bubbles.splice(i, 1);
        }
      }
    }
  }

  private updateShockwaves(dt: number): void {
    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const s = this.shockwaves[i];
      s.life -= dt;
      const progress = 1 - s.life / s.maxLife;
      s.radius = progress * s.maxRadius;

      if (s.life <= 0) {
        this.shockwaves.splice(i, 1);
      }
    }
  }

  private updateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.life -= dt;

      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
  }

  private checkCollisions(): void {
    // 1. Bullets vs Enemies / Bubbles
    for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const bullet = this.bullets[bi];

      // Check against untrapped enemies
      let bulletHit = false;
      for (let ei = this.enemies.length - 1; ei >= 0; ei--) {
        const enemy = this.enemies[ei];
        if (circleCircleIntersect(bullet, enemy)) {
          bulletHit = true;
          enemy.health -= 1;

          if (enemy.health <= 0) {
            // Encapsulate into Chrono-Bubble!
            this.trapEnemyInBubble(enemy);
            this.enemies.splice(ei, 1);
          } else {
            this.spawnExplosion(enemy.x, enemy.y, '#ffffff', 4);
          }
          break;
        }
      }

      if (bulletHit) {
        this.bullets.splice(bi, 1);
        continue;
      }

      // Check against trapped Bubbles
      for (let bui = this.bubbles.length - 1; bui >= 0; bui--) {
        const bubble = this.bubbles[bui];
        if (!bubble.popping && circleCircleIntersect(bullet, bubble)) {
          this.popBubble(bubble, true);
          this.bullets.splice(bi, 1);
          break;
        }
      }
    }

    // 2. Player Ramming vs Trapped Bubbles (Kinetic Ramming Twist!)
    for (let bui = this.bubbles.length - 1; bui >= 0; bui--) {
      const bubble = this.bubbles[bui];
      if (!bubble.popping && circleCircleIntersect(this.player, bubble)) {
        // Ramming pops bubble and applies huge kinetic recoil launch!
        const angle = Math.atan2(this.player.y - bubble.y, this.player.x - bubble.x);
        const ramRecoil = 400;
        this.player.recoilVx += Math.cos(angle) * ramRecoil;
        this.player.recoilVy += Math.sin(angle) * ramRecoil;

        this.popBubble(bubble, true);
      }
    }

    // 3. Shockwaves vs Bubbles (Chain Reaction Cascade!)
    for (const sw of this.shockwaves) {
      for (const bubble of this.bubbles) {
        if (!bubble.popping && circleCircleIntersect(sw, bubble)) {
          this.popBubble(bubble, true);
        }
      }
    }

    // 4. Untrapped Enemies vs Player
    if (this.player.invulnTimer <= 0) {
      for (let ei = this.enemies.length - 1; ei >= 0; ei--) {
        const enemy = this.enemies[ei];
        if (circleCircleIntersect(this.player, enemy)) {
          this.damagePlayer();
          this.spawnExplosion(enemy.x, enemy.y, enemy.color, 15);
          this.enemies.splice(ei, 1);
          break;
        }
      }
    }
  }

  trapEnemyInBubble(enemy: Enemy): void {
    enemy.trapped = true;
    enemy.trappedTimer = 0;

    const bubbleRadius = enemy.radius + 10;
    this.bubbles.push({
      id: this.nextId('bubble'),
      x: enemy.x,
      y: enemy.y,
      vx: (Math.random() - 0.5) * 20,
      vy: -30 - Math.random() * 20, // gentle floating upwards
      radius: bubbleRadius,
      maxRadius: bubbleRadius * 1.2,
      trappedEnemy: enemy,
      popping: false,
      popTimer: 0,
    });

    this.score += 20 * this.wave;
  }

  popBubble(bubble: Bubble, triggerShockwave = true): void {
    if (bubble.popping) return;
    bubble.popping = true;

    // Score calculation with combo chain multiplier
    const points = 100 * this.combo * this.wave;
    this.score += points;

    this.combo++;
    this.comboTimer = 2.5;

    this.spawnExplosion(
      bubble.x,
      bubble.y,
      bubble.trappedEnemy ? bubble.trappedEnemy.color : '#00ffff',
      20
    );

    if (triggerShockwave) {
      this.triggerShockwave(bubble.x, bubble.y, 110);
    }
  }

  triggerShockwave(x: number, y: number, maxRadius = 110): void {
    this.shockwaves.push({
      id: this.nextId('sw'),
      x,
      y,
      radius: 10,
      maxRadius,
      life: 0.35,
      maxLife: 0.35,
    });
  }

  damageCore(amount: number): void {
    this.core.health = Math.max(0, this.core.health - amount);
    this.message = 'CORE MATRIX UNDER ATTACK!';
    this.messageTimer = 1.0;

    if (this.core.health <= 0) {
      this.triggerGameOver();
    }
  }

  damagePlayer(): void {
    this.lives -= 1;
    this.player.invulnTimer = 2.0;

    // Knockback
    this.player.recoilVy += 250;

    if (this.lives <= 0) {
      this.triggerGameOver();
    } else {
      this.message = 'DEFENDER SHIELD BREACHED!';
      this.messageTimer = 1.5;
    }
  }

  private triggerGameOver(): void {
    this.status = 'gameover';
    this.message = 'GAME OVER - PRESS FIRE OR R TO RESTART';
    this.messageTimer = 999;
  }

  spawnExplosion(x: number, y: number, color: string, count = 12): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 220;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.3 + Math.random() * 0.4,
        maxLife: 0.7,
        color,
        size: 2 + Math.random() * 4,
      });
    }
  }
}
